import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BadgeCheck, User, Repeat } from 'lucide-react';
import { profilesAPI, followsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../lib/roles';

const socialUrl = (key, value) => {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const h = value.replace(/^@/, '');
  return { instagram: `https://instagram.com/${h}`, tiktok: `https://tiktok.com/@${h}`, facebook: `https://facebook.com/${h}` }[key] || null;
};

/** Public DJ / artist / organiser profile: /artists/:id */
export default function ArtistProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);

  useEffect(() => {
    profilesAPI.get(id).then(r => setP(r.data)).catch(() => navigate('/explore'));
  }, [id, navigate]);

  const toggleFollow = async () => {
    if (!user) return navigate(`/auth?redirect=/artists/${id}`);
    try {
      const r = p.is_following ? await followsAPI.unfollow('user', p.user_id) : await followsAPI.follow('user', p.user_id);
      setP(prev => ({ ...prev, is_following: r.data.following, followers_count: r.data.followers_count }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!p) return <div className="min-h-screen bg-[#050505] pt-24 text-center text-gray-400">Loading…</div>;

  const links = [
    ...Object.entries(p.social_links || {}).map(([k, v]) => [k, v, socialUrl(k, v)]),
    ...Object.entries(p.music_links || {}).map(([k, v]) => [k, v, /^https?:\/\//i.test(v || '') ? v : null]),
  ].filter(([, v]) => v);

  const EventList = ({ events }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      {events.map(e => (
        <Link key={e.event_id} to={`/events/${e.event_id}`} className="flex items-center gap-3 bg-[#0A0A0A] border border-gray-800 rounded-lg p-3 hover:border-red-500/60">
          {e.image_url ? <img src={e.image_url} alt="" className="w-14 h-14 rounded object-cover" /> : <div className="w-14 h-14 rounded bg-[#111]" />}
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{e.title}</p>
            <p className="text-gray-500 text-sm">{[e.date, e.city].filter(Boolean).join(' · ')}</p>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-16 page-enter">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-8">
          <div className="w-24 h-24 rounded-full bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0">
            {p.picture ? <img src={p.picture} alt={p.name} className="w-full h-full object-cover" /> : <User size={36} className="text-gray-500" />}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-black uppercase text-white flex items-center gap-2">
              {p.name} {p.is_verified && <BadgeCheck size={22} className="text-red-500" aria-label="Verified" />}
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {p.capabilities.map(c => (c === 'dj' ? 'DJ / Artist' : 'Organiser')).join(' · ')}{p.location ? ` · ${p.location}` : ''}
            </p>
            <p className="text-gray-500 text-sm">{p.followers_count} followers · {p.past_performances_count} past performances</p>
          </div>
          {(user?.user_id || user?.userId) !== p.user_id && (
            <button onClick={toggleFollow} className={p.is_following ? 'border border-gray-700 text-white px-5 py-2 rounded text-sm font-bold' : 'btn-primary px-5 py-2 rounded text-sm font-bold'}>
              {p.is_following ? 'Following' : 'Follow'}
            </button>
          )}
        </div>

        {p.bio && <p className="text-gray-200 leading-relaxed mb-6 max-w-3xl whitespace-pre-line">{p.bio}</p>}

        {links.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {links.map(([k, v, url]) => url ? (
              <a key={k} href={url} target="_blank" rel="noopener noreferrer" className="border border-gray-700 rounded-full px-3 py-1 text-xs text-gray-200 hover:border-red-500 capitalize">{k}</a>
            ) : (
              <span key={k} className="border border-gray-800 rounded-full px-3 py-1 text-xs text-gray-400"><span className="capitalize">{k}</span>: {v}</span>
            ))}
          </div>
        )}

        <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">UPCOMING PERFORMANCES</h2>
        {p.upcoming_performances.length === 0 ? <p className="text-gray-500 text-sm mb-8">No upcoming performances.</p> : <div className="mb-8"><EventList events={p.upcoming_performances} /></div>}

        {p.hosted_events.length > 0 && (
          <>
            <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">HOSTING</h2>
            <div className="mb-8"><EventList events={p.hosted_events} /></div>
          </>
        )}

        {[...p.series, ...p.performing_series].length > 0 && (
          <>
            <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">SERIES</h2>
            <div className="flex flex-wrap gap-2">
              {[...p.series, ...p.performing_series].map(s => (
                <span key={s.event_id} className="flex items-center gap-2 bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-sm text-white">
                  <Repeat size={14} className="text-red-500" /> {s.title}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
